import { Helmet } from 'react-helmet-async';

/**
 * P0-20: Reusable SEO component for every public page.
 *
 * Required on every route in a Casino-generated app. Populates <title>,
 * <meta description>, canonical URL, OG tags, Twitter card tags, and
 * optional JSON-LD structured data — the fundamentals for both classical
 * SEO (Google) and GEO (LLM citation).
 *
 * This lives in `casino/src/components/` today; in Phase E it gets extracted
 * into `@flowstack/sdk` so every app generated by the builder can import it.
 */

const DEFAULT_SITE_NAME = 'Ca$ino Builder';
const DEFAULT_BASE_URL = import.meta.env.VITE_SITE_URL || 'https://casino.flowstack.fun';

export interface SEOProps {
  /** Page title (without site suffix — the component appends it) */
  title: string;
  /** Meta description — should summarize the page in ~150 chars */
  description: string;
  /** Canonical URL for this page (absolute or path). Defaults to the current pathname. */
  canonicalUrl?: string;
  /** Absolute URL to the OG/Twitter share image */
  ogImage?: string;
  /** Optional comma-joined keywords */
  keywords?: string[];
  /** Optional JSON-LD structured data object(s). Pass the object, not a string. */
  jsonLd?: object | object[];
  /** Override the default site name in the <title> suffix */
  siteName?: string;
  /** No-index this page (staging, drafts, authenticated interior pages) */
  noIndex?: boolean;
}

export function SEO({
  title,
  description,
  canonicalUrl,
  ogImage,
  keywords,
  jsonLd,
  siteName = DEFAULT_SITE_NAME,
  noIndex = false,
}: SEOProps) {
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const canonical = canonicalUrl
    ? canonicalUrl.startsWith('http')
      ? canonicalUrl
      : `${DEFAULT_BASE_URL}${canonicalUrl}`
    : typeof window !== 'undefined'
      ? `${DEFAULT_BASE_URL}${window.location.pathname}`
      : DEFAULT_BASE_URL;
  const image = ogImage || `${DEFAULT_BASE_URL}/og-image.png`;

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLdArray.map((ld, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}
